import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import {
  getIntegratedEntityMappings,
  getIntegrationsManifest,
} from 'lane-shared/graphql/query';

const INTEGRATED_ENTITY_TYPE_TENANT = 'INTEGRATED_ENTITY_TYPE_TENANT';

interface EntityMapping {
  vtsEntityId?: string;
  cesSei?: {
    sourceEntityId: string;
    sourceEntityType: string;
    sourceType: string;
  };
  thirdPartyEntityId: string;
  integratedEntityType: string;
}

interface UseEntityMappingsProps {
  channelId?: string;
  enabled?: boolean;
}

interface UseEntityMappingsReturn {
  entityMappings: EntityMapping[];
}

export const useEntityMappings = ({
  channelId,
  enabled = true,
}: UseEntityMappingsProps): UseEntityMappingsReturn => {
  // Fetch integrations manifest to get the correct integration ID
  const { data: integrationsManifestData } = useQuery(getIntegrationsManifest, {
    variables: { channelId, domain: 'DOMAIN_ACCESS' },
    skip: !enabled || !channelId,
    errorPolicy: 'all',
  });

  // Extract integration ID from manifest or use fallback
  const manifestIntegrationId = useMemo(() => {
    const integrations =
      integrationsManifestData?.getIntegrationsManifest?.integrations;
    const integrationId = integrations?.[0]?.vtsIntegrationId;

    return integrationId || undefined;
  }, [integrationsManifestData]);

  // Fetch entity mappings using the integration ID
  const { data } = useQuery(getIntegratedEntityMappings, {
    variables: manifestIntegrationId
      ? {
          vtsIntegrationId: String(manifestIntegrationId),
          integratedEntityType: INTEGRATED_ENTITY_TYPE_TENANT,
        }
      : {},
    skip: !enabled || !manifestIntegrationId,
    errorPolicy: 'all',
  });

  const entityMappings =
    data?.getIntegratedEntityMappings?.entityMappings || [];

  return {
    entityMappings,
  };
};
